// 구르기 잔상 리본(REL-021): 영웅의 실제 렌더 위치를 매 프레임 표본으로 받아 몸 뒤로 짧은(약 0.15초 ≈ 2m) 세로 띠를 그린다.
// 고정 위치 스프라이트는 몸이 그 안으로 굴러 들어가 관절·칼을 가렸다(2026-10-19 기준 캡처). 이 띠는 몸 바로 뒤 0.35m를 비우고 꼬리로 갈수록 사라진다.
// 표시 계층만: 회피 거리·무적·쿨은 게임(src/game)이 정하고 여기서는 읽지 않는다. 프레임 루프에서 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { attribute, float, smoothstep, uniform, vec3 } from 'three/tsl';

/** 띠 설정(구르기 기본값은 DODGE_OPTS) */
export interface TrailOpts {
  /** 표본 수명(초) */
  life?: number;
  /** 몸 바로 뒤 비우는 길이(m) */
  gap?: number;
  /** 띠 높이(m) */
  y0?: number; y1?: number;
  /** 표본을 더하는 시간(틱, 동작 길이) / 띠를 그리는 최대 시간(틱) */
  sampleTicks?: number; activeTicks?: number;
  /** 몸통·가운데 심 색(선형 rgb)과 불투명도 배율 */
  body?: readonly [number, number, number];
  core?: readonly [number, number, number];
  opacity?: number;
}

/** 표본 수(링 버퍼) */
const N = 24;
/** 표본 수명(초, 영웅 시간): 이보다 오래된 표본은 띠에서 빠진다(0.15초는 밝은 돌바닥에서 거의 안 보였다) */
const LIFE = 0.25;
/** 몸 바로 뒤 비우는 길이(m): 구르는 몸·칼과 겹치지 않게 */
const GAP = 0.25;
/** 띠 높이(m): 구르는 몸 높이 */
const Y0 = 0.18, Y1 = 1.0;
/** 리본을 그리는 최대 시간(틱): 회피 350ms(21틱) + 꼬리 */
const ACTIVE_TICKS = 30;
const DODGE_OPTS: Required<TrailOpts> = {
  life: LIFE, gap: GAP, y0: Y0, y1: Y1, sampleTicks: 21, activeTicks: ACTIVE_TICKS,
  body: [0.95, 0.32, 0.42], core: [1, 0.93, 0.95], opacity: 1,
};

export class DodgeTrail {
  readonly mesh: THREE.Mesh;
  private readonly pos: Float32Array;
  private readonly fade: Float32Array;
  private readonly sx = new Float32Array(N);
  private readonly sz = new Float32Array(N);
  private readonly st = new Float32Array(N);
  private head = 0;
  private count = 0;
  private start = -1e9;
  private active = false;
  /** 섬광 약화: 띠 밝기 배율 */
  readonly gain = uniform(1);
  private readonly o: Required<TrailOpts>;

  /** opts 없이 만들면 구르기 잔상(이전 값 그대로). 2026-10-27 전투 액션 VFX: 돌진·런처·도약 잔상이 같은 띠를 값만 바꿔 쓴다 */
  constructor(opts: TrailOpts = {}) {
    this.o = { ...DODGE_OPTS, ...opts };
    const g = new THREE.BufferGeometry();
    this.pos = new Float32Array(N * 2 * 3);
    this.fade = new Float32Array(N * 2);
    const vv = new Float32Array(N * 2);
    for (let i = 0; i < N; i++) { vv[i * 2] = 0; vv[i * 2 + 1] = 1; }
    const idx: number[] = [];
    for (let i = 0; i < N - 1; i++) {
      const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
      idx.push(a, c, b, b, c, d);
    }
    const pa = new THREE.BufferAttribute(this.pos, 3); pa.setUsage(THREE.DynamicDrawUsage);
    const fa = new THREE.BufferAttribute(this.fade, 1); fa.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', pa);
    g.setAttribute('aFade', fa);
    g.setAttribute('aV', new THREE.BufferAttribute(vv, 1));
    g.setIndex(idx);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    // 일반 투명 혼합: 가산 혼합은 밝은 돌바닥(R1)에서 더할 밝기가 없어 보이지 않았다(2026-10-19 픽스처 캡처)
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    m.fog = false;
    const f = attribute('aFade', 'float');
    const v = attribute('aV', 'float');
    // 세로: 가운데가 가장 밝고 위·아래 가장자리는 0(딱딱한 선 없음). 흰빛 도는 옅은 진홍(영웅색)
    const band = smoothstep(float(0), float(0.35), v).mul(float(1).sub(smoothstep(float(0.65), float(1), v)));
    const core = smoothstep(float(0.4), float(0.5), v).mul(float(1).sub(smoothstep(float(0.5), float(0.6), v)));
    const [br, bg, bb] = this.o.body, [cr, cg, cb] = this.o.core;
    m.colorNode = vec3(br, bg, bb).mul(float(1).sub(core)).add(vec3(cr, cg, cb).mul(core));
    m.opacityNode = band.mul(0.8).add(core.mul(0.2)).min(0.9).mul(f).mul(this.gain).mul(this.o.opacity);
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 19;
    this.mesh.visible = false;
  }

  /** Ev.Dodge 순간(영웅 시계 틱) */
  begin(now: number, x: number, z: number): void {
    this.start = now;
    this.active = true;
    this.count = 0;
    this.head = 0;
    this.push(x, z, now);
  }

  private push(x: number, z: number, now: number): void {
    const i = this.head;
    this.sx[i] = x; this.sz[i] = z; this.st[i] = now;
    this.head = (i + 1) % N;
    if (this.count < N) this.count++;
  }

  /** 매 렌더 프레임: now 영웅 시계(틱), 영웅 렌더 위치 */
  update(now: number, x: number, y: number, z: number): void {
    if (!this.active) return;
    // 렌더 영웅 시계는 보간 때문에 이벤트 틱보다 최대 1틱 늦다: 음수 경과는 0으로 본다(그대로 끄면 리본이 첫 프레임에 사라졌다)
    const age0 = Math.max(0, now - this.start);
    if (age0 > this.o.activeTicks || now - this.start < -3) { this.active = false; this.mesh.visible = false; return; }
    // 회피 시간(21틱) 동안만 표본을 더한다. 그 뒤엔 꼬리만 사라진다
    if (age0 <= this.o.sampleTicks) {
      const last = (this.head + N - 1) % N;
      const dx = x - this.sx[last], dz = z - this.sz[last];
      if (dx * dx + dz * dz > 0.0025) this.push(x, z, now);
    }
    const lifeT = this.o.life * 60;
    const Y0 = this.o.y0, Y1 = this.o.y1, GAP = this.o.gap;
    // 최신 → 과거 순으로 정점을 채운다. 몸에서 GAP 이내는 0, 수명에 따라 꼬리 0
    const p = this.pos, f = this.fade;
    let dist = 0, px = x, pz = z, used = 0;
    for (let k = 0; k < N; k++) {
      let sx = px, sz = pz, age = lifeT;
      if (k < this.count) {
        const i = (this.head + N - 1 - k) % N;
        sx = this.sx[i]; sz = this.sz[i]; age = now - this.st[i];
        if (age < 0) age = 0;
      }
      dist += Math.hypot(sx - px, sz - pz);
      px = sx; pz = sz;
      const o = k * 6;
      p[o] = sx; p[o + 1] = y + Y0; p[o + 2] = sz;
      p[o + 3] = sx; p[o + 4] = y + Y1; p[o + 5] = sz;
      const near = Math.min(1, Math.max(0, (dist - GAP) / 0.2));
      const tail = Math.max(0, 1 - age / lifeT);
      const a = near * tail;
      f[k * 2] = a; f[k * 2 + 1] = a;
      if (a > 0.01) used++;
    }
    this.mesh.visible = used > 1;
    (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.mesh.geometry.getAttribute('aFade') as THREE.BufferAttribute).needsUpdate = true;
  }

  get isActive(): boolean { return this.active; }

  clear(): void { this.active = false; this.mesh.visible = false; this.count = 0; }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

